import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { updateProfil } from '../api';
import SektionenTool from './tools/SektionenTool';
import TonlageTool from './tools/TonlageTool';
import { MessageSquareWarning } from './animate-ui/icons/MessageSquareWarning';
import { UserRound } from './animate-ui/icons/UserRound';
import { ToggleLeft } from './animate-ui/icons/ToggleLeft';

const APP_STORE_URL = 'https://apps.apple.com/de/app/cvflow-lebenslauf-mit-ki/id6778375720';


const AppleIcon = ({ size = 24, color = '#000000' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_4418_10204)">
      <path fillRule="evenodd" clipRule="evenodd" d="M15.4 2C15.6 3.1 15.1 4.2 14.5 5C13.9 5.8 12.8 6.5 11.7 6.4C11.5 5.3 12 4.3 12.6 3.5C13.3 2.7 14.4 2.1 15.4 2ZM18.6 19.4C19.1 18.6 19.4 18.1 19.8 17.2C16.7 16 16.2 11.6 19.3 10C18.4 8.8 17.1 8.2 15.8 8.2C14.9 8.2 14.3 8.4 13.7 8.6C13.2 8.8 12.8 8.9 12.3 8.9C11.7 8.9 11.3 8.7 10.7 8.5C10.1 8.3 9.50004 8.1 8.80004 8.1C7.40004 8.1 5.90004 8.9 5.00004 10.4C3.70004 12.4 3.90004 16.3 6.00004 19.5C6.90004 20.7 7.90004 22 9.20004 22C9.80004 22 10.1 21.8 10.5 21.7C11 21.5 11.5 21.3 12.3 21.3C13.2 21.3 13.6 21.5 14.1 21.7C14.5 21.9 14.8 22 15.4 22C16.8 22 17.8 20.5 18.6 19.4Z" stroke={color} strokeWidth="2.3" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <defs>
      <clipPath id="clip0_4418_10204"><rect width="24" height="24" fill="white"/></clipPath>
    </defs>
  </svg>
);

const EinstellungenIcon = ({ size = 24, color = '#000000' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_tp_einstell)">
      <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 12.88V11.12C2 10.08 2.85 9.22 3.9 9.22C5.71 9.22 6.45 7.94 5.54 6.37C5.02 5.47 5.33 4.3 6.24 3.78L7.97 2.79C8.76 2.32 9.78 2.6 10.25 3.39L10.36 3.58C11.26 5.15 12.74 5.15 13.65 3.58L13.76 3.39C14.23 2.6 15.25 2.32 16.04 2.79L17.77 3.78C18.68 4.3 18.99 5.47 18.47 6.37C17.56 7.94 18.3 9.22 20.11 9.22C21.15 9.22 22.01 10.07 22.01 11.12V12.88C22.01 13.92 21.16 14.78 20.11 14.78C18.3 14.78 17.56 16.06 18.47 17.63C18.99 18.54 18.68 19.7 17.77 20.22L16.04 21.21C15.25 21.68 14.23 21.4 13.76 20.61L13.65 20.42C12.75 18.85 11.27 18.85 10.36 20.42L10.25 20.61C9.78 21.4 8.76 21.68 7.97 21.21L6.24 20.22C5.33 19.7 5.02 18.53 5.54 17.63C6.45 16.06 5.71 14.78 3.9 14.78C2.85 14.78 2 13.92 2 12.88Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </g>
    <defs><clipPath id="clip0_tp_einstell"><rect width="24" height="24" fill="white"/></clipPath></defs>
  </svg>
);


const STUDIO_TIPPS = [
  <>Verwende den <em>"Templates"</em> Tab, um dein Anschreiben an das Branding deines Wunschunternehmens anzupassen.</>,
  <>Je genauer deine Angaben zu Stelle und Unternehmen, desto <em>treffender</em> werden deine Texte.</>,
  <>Markiere als <em>„Abgeschickt"</em>, sobald du eine Bewerbung versendet hast.</>,
];

function StudioTipps() {
  const [idx, setIdx] = useState(0);
  const [sichtbar, setSichtbar] = useState(true);
  const [shimmerKey, setShimmerKey] = useState(0);
  const [offen, setOffen] = useState(true);
  const timerRef = useRef(null);
  const shimmerRef = useRef(null);

  const idxRef = useRef(0);

  const wechseln = (naechster) => {
    clearTimeout(timerRef.current);
    setSichtbar(false);
    timerRef.current = setTimeout(() => {
      idxRef.current = naechster;
      setIdx(naechster);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        setSichtbar(true);
        shimmerRef.current = setTimeout(() => setShimmerKey(k => k + 1), 1000);
      }));
    }, 2000);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      wechseln((idxRef.current + 1) % STUDIO_TIPPS.length);
    }, 10000);
    return () => {
      clearInterval(interval);
      clearTimeout(timerRef.current);
      clearTimeout(shimmerRef.current);
    };
  }, []);

  return (
    <div className="studio-tipps-section">
      <span className="studio-panel-heading">STUDIO</span>
      <div className="tipps-box">
        <div className="tipps-box-header">
          <span className="tipps-box-label">Studio Tipps</span>
          <button
            className={`tipps-toggle-btn${offen ? ' tipps-toggle-btn--open' : ''}`}
            onClick={() => setOffen(v => !v)}
            title={offen ? 'Ausblenden' : 'Einblenden'}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M2 4L5 7L8 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        {offen && (
          <>
            <div className="tipps-loop-wrap">
              {STUDIO_TIPPS.map((tipp, i) => (
                <div
                  key={i}
                  className={i === idx
                    ? `tipps-karte${sichtbar ? '' : ' tipps-karte--out'}`
                    : 'tipps-karte tipps-karte--hidden'}
                >
                  {i === idx ? (
                    <span key={shimmerKey} className={shimmerKey > 0 ? 'tipps-shimmer-span' : ''}>
                      {tipp}
                    </span>
                  ) : tipp}
                </div>
              ))}
            </div>
            <div className="tipps-dots">
              {STUDIO_TIPPS.map((_, i) => (
                <span
                  key={i}
                  className={`tipps-dot${i === idx ? ' tipps-dot--active' : ''}`}
                  onClick={() => wechseln(i)}
                  style={{ cursor: 'pointer' }}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StudioActionCard({ variant, onClick }) {
  return (
    <button type="button" className={`sac-card sac-card--${variant}`} onClick={onClick}>
      <span className="sac-title">{variant === 'cv' ? 'Lebenslauf' : 'Anschreiben'}</span>
      <div className={`sac-doc sac-doc--${variant}`}>
        {variant === 'cv' ? (
          <div className="sac-doc-inner sac-doc-inner--cv">
            <div className="sac-cv-header">
              <div className="sac-cv-headlines">
                <div className="sac-cap sac-cv-name" />
                <div className="sac-cap sac-cv-contact" />
              </div>
              <div className="sac-cv-photo" />
            </div>
            <div className="sac-cv-intro">
              <div className="sac-cap" style={{ width: '100%' }} />
              <div className="sac-cap" style={{ width: '100%' }} />
              <div className="sac-cap" style={{ width: '100%' }} />
              <div className="sac-cap" style={{ width: '68%' }} />
            </div>
            <div className="sac-cv-heading" style={{ width: '48%' }} />
            <div className="sac-cv-cols">
              <div className="sac-cv-col">
                <div className="sac-cv-item">
                  <div className="sac-cap sac-cv-subhead" style={{ width: '52%' }} />
                  <div className="sac-cap" style={{ width: '82%' }} />
                  <div className="sac-cap" style={{ width: '64%' }} />
                </div>
                <div className="sac-cv-item">
                  <div className="sac-cap sac-cv-subhead" style={{ width: '58%' }} />
                  <div className="sac-cap" style={{ width: '70%' }} />
                  <div className="sac-cap" style={{ width: '50%' }} />
                </div>
                <div className="sac-cv-item">
                  <div className="sac-cap sac-cv-subhead" style={{ width: '44%' }} />
                  <div className="sac-cap" style={{ width: '76%' }} />
                </div>
              </div>
              <div className="sac-cv-col">
                <div className="sac-cv-item">
                  <div className="sac-cap sac-cv-subhead" style={{ width: '48%' }} />
                  <div className="sac-cap" style={{ width: '68%' }} />
                  <div className="sac-cap" style={{ width: '84%' }} />
                </div>
                <div className="sac-cv-item">
                  <div className="sac-cap sac-cv-subhead" style={{ width: '56%' }} />
                  <div className="sac-cap" style={{ width: '62%' }} />
                  <div className="sac-cap" style={{ width: '46%' }} />
                </div>
                <div className="sac-cv-item">
                  <div className="sac-cap sac-cv-subhead" style={{ width: '72%' }} />
                  <div className="sac-cap" style={{ width: '58%' }} />
                  <div className="sac-cap" style={{ width: '40%' }} />
                </div>
              </div>
            </div>
            <div className="sac-cv-heading" style={{ width: '42%' }} />
            <div className="sac-cv-exp">
              <div className="sac-cv-exp-head">
                <div className="sac-cap sac-cv-subhead" style={{ width: '54%' }} />
                <div className="sac-cap sac-cv-date" style={{ width: '22%' }} />
              </div>
              <div className="sac-cv-bullets">
                <div className="sac-cap" style={{ width: '94%' }} />
                <div className="sac-cap" style={{ width: '88%' }} />
              </div>
            </div>
            <div className="sac-cv-exp">
              <div className="sac-cv-exp-head">
                <div className="sac-cap sac-cv-subhead" style={{ width: '60%' }} />
                <div className="sac-cap sac-cv-date" style={{ width: '22%' }} />
              </div>
              <div className="sac-cv-bullets">
                <div className="sac-cap" style={{ width: '96%' }} />
                <div className="sac-cap" style={{ width: '82%' }} />
                <div className="sac-cap" style={{ width: '90%' }} />
              </div>
            </div>
            <div className="sac-cv-exp">
              <div className="sac-cv-exp-head">
                <div className="sac-cap sac-cv-subhead" style={{ width: '50%' }} />
                <div className="sac-cap sac-cv-date" style={{ width: '22%' }} />
              </div>
              <div className="sac-cv-bullets">
                <div className="sac-cap" style={{ width: '92%' }} />
                <div className="sac-cap" style={{ width: '86%' }} />
                <div className="sac-cap" style={{ width: '78%' }} />
              </div>
            </div>
          </div>
        ) : (
          <div className="sac-doc-inner sac-doc-inner--an">
            <div className="sac-an-group sac-an-group--right">
              <div className="sac-cap" style={{ width: '34%' }} />
              <div className="sac-cap" style={{ width: '38%' }} />
              <div className="sac-cap" style={{ width: '30%' }} />
              <div className="sac-cap" style={{ width: '36%' }} />
            </div>
            <div className="sac-an-group sac-an-group--left">
              <div className="sac-cap" style={{ width: '36%' }} />
              <div className="sac-cap" style={{ width: '32%' }} />
              <div className="sac-cap" style={{ width: '38%' }} />
              <div className="sac-cap" style={{ width: '30%' }} />
            </div>
            <div className="sac-cap sac-cap--betreff" />
            <div className="sac-an-para">
              <div className="sac-cap" style={{ width: '100%' }} />
              <div className="sac-cap" style={{ width: '97%' }} />
              <div className="sac-cap" style={{ width: '100%' }} />
              <div className="sac-cap" style={{ width: '94%' }} />
              <div className="sac-cap" style={{ width: '100%' }} />
              <div className="sac-cap" style={{ width: '98%' }} />
              <div className="sac-cap" style={{ width: '95%' }} />
              <div className="sac-cap" style={{ width: '100%' }} />
              <div className="sac-cap" style={{ width: '96%' }} />
              <div className="sac-cap" style={{ width: '100%' }} />
              <div className="sac-cap" style={{ width: '93%' }} />
              <div className="sac-cap" style={{ width: '99%' }} />
              <div className="sac-cap" style={{ width: '96%' }} />
              <div className="sac-cap" style={{ width: '100%' }} />
              <div className="sac-cap" style={{ width: '68%' }} />
            </div>
            <div className="sac-an-group sac-an-group--left">
              <div className="sac-cap" style={{ width: '30%' }} />
              <div className="sac-cap" style={{ width: '40%' }} />
            </div>
          </div>
        )}
      </div>
    </button>
  );
}

const VERF_PRESETS = ['ab sofort', 'flexibel'];

function formatVerfuegbarkeit(wert) {
  if (!wert) return '';
  if (VERF_PRESETS.includes(wert)) return wert;
  try {
    const d = new Date(wert + 'T00:00:00');
    return 'ab ' + d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return wert; }
}

function VerfuegbarkeitWidget({ profil, onSaved }) {
  const [offen, setOffen] = useState(false);
  const [saving, setSaving] = useState(false);
  const wert = profil?.verfuegbarkeit || '';
  const isDatum = wert && !VERF_PRESETS.includes(wert);

  if (!isDatum) return null;

  const speichern = async (neuerWert) => {
    if (!profil?.dateiname) return;
    setSaving(true);
    try {
      await updateProfil(profil.dateiname, { verfuegbarkeit: neuerWert });
      if (onSaved) onSaved();
      setOffen(false);
    } finally {
      setSaving(false);
    }
  };

  const heute = new Date();
  heute.setHours(0, 0, 0, 0);

  const navigateDatum = (richtung) => {
    const d = new Date(wert);
    let ziel;
    if (richtung === 'prev-1') {
      ziel = d.getDate() === 1
        ? new Date(d.getFullYear(), d.getMonth() - 1, 1)
        : new Date(d.getFullYear(), d.getMonth(), 1);
    } else if (richtung === 'prev-15') {
      ziel = d.getDate() <= 15
        ? new Date(d.getFullYear(), d.getMonth() - 1, 15)
        : new Date(d.getFullYear(), d.getMonth(), 15);
    } else if (richtung === 'next-15') {
      ziel = d.getDate() >= 15
        ? new Date(d.getFullYear(), d.getMonth() + 1, 15)
        : new Date(d.getFullYear(), d.getMonth(), 15);
    } else {
      ziel = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    }
    if (ziel < heute) ziel = new Date(heute);
    speichern(ziel.toISOString().split('T')[0]);
  };

  const istDisabled = (richtung) => {
    const d = new Date(wert);
    let ziel;
    if (richtung === 'prev-1') {
      ziel = d.getDate() === 1
        ? new Date(d.getFullYear(), d.getMonth() - 1, 1)
        : new Date(d.getFullYear(), d.getMonth(), 1);
    } else {
      ziel = d.getDate() <= 15
        ? new Date(d.getFullYear(), d.getMonth() - 1, 15)
        : new Date(d.getFullYear(), d.getMonth(), 15);
    }
    return ziel < heute;
  };

  return (
    <div className="studio-section studio-section--verf">
      <div className="studio-section-label">Startdatum</div>
      {!offen ? (
        <button
          className="verf-display-btn"
          onClick={() => setOffen(true)}
          title="Klicken zum Ändern"
        >
          <span className="verf-display-icon">📅</span>
          <span className="verf-display-val">{formatVerfuegbarkeit(wert)}</span>
          <span className="verf-display-edit">✎</span>
        </button>
      ) : (
        <div className="verf-edit-wrap">
          <input
            className="verf-date-input"
            type="date"
            defaultValue={wert}
            onChange={e => e.target.value && speichern(e.target.value)}
            disabled={saving}
          />
          <button className="verf-cancel-btn" onClick={() => setOffen(false)}>Abbrechen</button>
        </div>
      )}
      <div className="verf-nav-row">
        <button className="verf-nav-btn" onClick={() => navigateDatum('prev-1')}  disabled={saving || istDisabled('prev-1')}>«  1.</button>
        <button className="verf-nav-btn" onClick={() => navigateDatum('prev-15')} disabled={saving || istDisabled('prev-15')}>‹ 15.</button>
        <button className="verf-nav-btn" onClick={() => navigateDatum('next-15')} disabled={saving}>15. ›</button>
        <button className="verf-nav-btn" onClick={() => navigateDatum('next-1')}  disabled={saving}>1. »</button>
      </div>
    </div>
  );
}

export function PanelActions({ onFeedbackOeffnen, darkMode, onToggleDarkMode, onProfilOeffnen }) {
  const navigate = useNavigate();
  const [qrModalOffen, setQrModalOffen] = useState(false);

  return (
    <div id="tutorial-panel-actions" className="panel-actions">
      <button className="topbar-icon-btn topbar-icon-btn--appstore" title="App herunterladen" onClick={() => setQrModalOffen(true)}>
        <span className="appstore-face appstore-face--apple"><AppleIcon size={25} color="currentColor" /></span>
        <span className="appstore-face appstore-face--qr">
          <QRCodeSVG value="CVFlow" size={18} level="L" includeMargin={false} bgColor="transparent" fgColor="currentColor" />
        </span>
      </button>
      <button className="topbar-icon-btn topbar-icon-btn--idea" title="Feature wünschen / Bug melden" onClick={onFeedbackOeffnen}>
        <MessageSquareWarning size={24} strokeWidth={2.4} animateOnHover />
      </button>
      <button
        className="topbar-icon-btn topbar-icon-btn--toggle"
        title={darkMode ? 'Light Mode' : 'Dark Mode'}
        onClick={onToggleDarkMode}
      >
        <ToggleLeft size={32} toggled={darkMode} animateOnHover />
      </button>
      <button className="topbar-icon-btn topbar-icon-btn--profile" title="Profil öffnen" onClick={onProfilOeffnen}>
        <UserRound size={28} animateOnHover />
      </button>
      <button
        className="topbar-icon-btn topbar-icon-btn--settings"
        onClick={() => navigate('/profileinstellungen')}
        title="Einstellungen"
      >
        <EinstellungenIcon size={26} color="currentColor" />
      </button>

      {qrModalOffen && (
        <div className="qr-modal-overlay" onClick={() => setQrModalOffen(false)}>
          <div className="qr-modal" onClick={e => e.stopPropagation()}>
            <button className="qr-modal-close" onClick={() => setQrModalOffen(false)}>✕</button>
            <div className="qr-modal-qr">
              <QRCodeSVG value={APP_STORE_URL} size={220} level="M" includeMargin={false} />
            </div>
            <strong className="qr-modal-title">Bleib mit <em>CVFlow</em> mobil</strong>
            <span className="qr-modal-sub">Lade dir jetzt die App im App Store runter</span>
          </div>
        </div>
      )}
    </div>
  );
}

function ToolPanel({ tonlage, onTonlageChange, nutzer, onProfilOeffnen, profil, onProfilRefresh, width, onFeedbackOeffnen, darkMode, onToggleDarkMode }) {
  return (
    <div className="tool-panel-wrapper" style={width ? { width } : undefined}>
      {/* ── Panel Actions ──────────────────────────── */}
      <PanelActions
        onFeedbackOeffnen={onFeedbackOeffnen}
        darkMode={darkMode}
        onToggleDarkMode={onToggleDarkMode}
        onProfilOeffnen={onProfilOeffnen}
      />

      <div className="tool-panel-scroll">
        {/* ── Studio Tipps ───────────────────────────── */}
        <StudioTipps />

        {/* ── Studio Actions (Platzhalter) ──────────────────────────── */}
        <div className="sac-section">
          <StudioActionCard variant="cv" />
          <StudioActionCard variant="an" />
        </div>

        {/* ── Verfügbarkeit ──────────────────────────── */}
        <VerfuegbarkeitWidget profil={profil} onSaved={onProfilRefresh} />
      </div>
    </div>
  );
}

export default ToolPanel;
